import { sendCriteoEvent } from '@ma-js-common/criteo-helpers';

import MapboxMap from './map/mapbox-map.es6';
import HTTPService from './services/HTTPService.es6';
import {
    MAX_PAST_SALES_FETCHED,
    OTHER_PAST_SALE_FEATURE_ID,
    REFERENCE_FEATURE_ID,
    RELEVANT_PAST_SALE_FEATURE_ID,
} from './constants.es6';
import { isMobile } from './device';
import Wa from './wa';

/**
 * Get realtor coordinates from map element.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 *
 * @returns {Object} realtor coordinates
 */
function getRealtorCoordinates(wrapperElement) {
    return {
        lat: Number(wrapperElement.getAttribute('data-realtor-coordinates-lat')),
        lng: Number(wrapperElement.getAttribute('data-realtor-coordinates-lng')),
    };
}

/**
 * Get habitations coordinates from map element.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 *
 * @returns {Object} Coordinates
 */
function getHabitationCoordinates(wrapperElement) {
    const strCoordinates = wrapperElement.getAttribute('data-habitation-coordinates');
    if (!strCoordinates) {
        return undefined;
    }

    const rawCoordinates = strCoordinates.split(',');
    return { lng: Number(rawCoordinates[0]), lat: Number(rawCoordinates[1]) };
}

/**
 * Create a map centered on the realtor coordinates.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 * @param {Number}     realtorId id of the displayed realtor
 * @param {Object}     mapOptions additional options for the MapboxMap constructor
 *
 * @returns {MapboxMap} created MapboxMap instance
 */
function initializeRealtorMap(wrapperElement, realtorId, mapOptions) {
    return new MapboxMap(wrapperElement, `map_${realtorId}`, {
        center: getRealtorCoordinates(wrapperElement),
        interactive: wrapperElement.hasAttribute('data-vector-map-interactive'),
        zoom: Number(wrapperElement.getAttribute('data-vector-map-zoom-level')),
        scrollZoom: wrapperElement.hasAttribute('data-vector-map-scroll-zoom'),
        showControls: wrapperElement.hasAttribute('data-vector-map-show-controls'),
        ...mapOptions,
    });
}

/**
 * Handle map marker click
 * @param {Function} callback to invoke
 * @param {[]Object} info all the arguments received from clickHandler
 * @param {String} eventLabel event label for google analytics
 */
function handleMapMarkerClick(callback, info, eventLabel = 'past_sales:marker') {
    if (!callback) return null;
    const src = info[1].properties.url;
    return src ? callback(src, eventLabel) : null;
}

/**
 * Display the recent past sales of a realtor on a map.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 * @param {Number}     realtorId id of the displayed realtor
 * @param {Function} onMapMarkerClick callback invoked when map markers clicked
 */
function initializeClassicPastSalesMap(wrapperElement, realtorId, onMapMarkerClick) {
    const realtorMap = initializeRealtorMap(wrapperElement, realtorId);

    const featuresURLParams = {
        realtor_id: realtorId,
        realtor_products: 'realtor-directory',
        page_size: '500',
    };

    const realtorCoordinates = getRealtorCoordinates(wrapperElement);

    realtorMap
        .registerFeatures(REFERENCE_FEATURE_ID, '/carte/biens-vendus', featuresURLParams, {
            updateOnBoundsChanged: false,
            fitClosestFeaturesAtPoint: realtorCoordinates,
            clickHandler: (...clickInfos) => handleMapMarkerClick(onMapMarkerClick, clickInfos),
            infoWindowParameters: {
                ga_category: wrapperElement.getAttribute('data-ua-event-category'),
                ga_label_prefix: wrapperElement.getAttribute('data-ua-event-label-prefix'),
                wa_name_prefix: wrapperElement.getAttribute('data-wa-name-prefix'),
                wa_source: wrapperElement.getAttribute('data-wa-source'),
                types: REFERENCE_FEATURE_ID,
                realtor_id: realtorId,
            },
        })
        .then(
            () =>
                wrapperElement.hasAttribute('data-realtor-is-public-address') &&
                realtorMap.registerExternalMarker(
                    'loaded-realtor-marker',
                    '/static/images/realtor-marker@2x.png',
                    realtorCoordinates,
                ),
        );
}

/**
 * Register a past sales feature layer on a realtor MapboxMap object.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 * @param {Number}     realtorId id of the displayed realtor
 * @param {MapboxMap}  realtorMap realtor MapboxMap
 * @param {String}     layerId feature layer Id
 * @param {[]Object}   pastSales list of past sales
 * @param {Object}     featureOptions additional options for the feature layer registration
 * @param {Function}   onMapMarkerClick callback for when map marker is clicked
 *
 * @returns {Promise}
 */
function registerPastSalesFeatures(
    wrapperElement,
    realtorId,
    realtorMap,
    layerId,
    pastSales,
    featureOptions,
    onMapMarkerClick,
) {
    const coordinates =
        getHabitationCoordinates(wrapperElement) || getRealtorCoordinates(wrapperElement);

    return realtorMap.registerFeatures(
        layerId,
        { type: 'FeatureCollection', features: pastSales },
        undefined,
        {
            updateOnBoundsChanged: false,
            fitClosestFeaturesAtPoint: coordinates,
            clickHandler: (...clickInfos) => handleMapMarkerClick(onMapMarkerClick, clickInfos),
            infoWindowParameters: {
                ga_category: wrapperElement.getAttribute('data-ua-event-category'),
                ga_label_prefix: wrapperElement.getAttribute('data-ua-event-label-prefix'),
                wa_name_prefix: wrapperElement.getAttribute('data-wa-name-prefix'),
                wa_source: wrapperElement.getAttribute('data-wa-source'),
                types: REFERENCE_FEATURE_ID,
                realtor_id: realtorId,
            },
            ...featureOptions,
        },
    );
}

/**
 * Display the relevant (green) and recent (grey) past sales of a realtor on a map regarding a place.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 * @param {Number}     realtorId id of the displayed realtor
 */
function initializeRelevantPastSalesMap(wrapperElement, realtorId, onMapMarkerClick) {
    const centerCoordinates =
        getHabitationCoordinates(wrapperElement) || getRealtorCoordinates(wrapperElement);
    const relevantPastSalesIds = wrapperElement
        .getAttribute('data-relevant-past-sales-ids')
        .split(',')
        .map(Number);
    const featuresURLParams = {
        realtor_id: realtorId,
        page_size: MAX_PAST_SALES_FETCHED,
        // We should not need to send the realtor_products parameter to the endpoint
        // that gives the past sales geographic coordinates for a realtor
        // but we must because the endpoint is used for multiple needs
        // and will not give expected data without this parameter
        realtor_products: 'realtor-directory',
    };

    const pastSalesPromise = HTTPService.get('/carte/biens-vendus', featuresURLParams).then(
        ({ data: { features: pastSales } }) => {
            const otherPastSales = [];
            const relevantPastSales = [];

            pastSales.forEach(pastSale => {
                if (relevantPastSalesIds.includes(pastSale.id)) {
                    relevantPastSales.push(pastSale);
                } else {
                    otherPastSales.push(pastSale);
                }
            });

            return [otherPastSales, relevantPastSales];
        },
    );

    function onLoad(_, realtorMap, resolve) {
        pastSalesPromise
            .then(([otherPastSales, relevantPastSales]) =>
                Promise.all([
                    registerPastSalesFeatures(
                        wrapperElement,
                        realtorId,
                        realtorMap,
                        RELEVANT_PAST_SALE_FEATURE_ID,
                        relevantPastSales,
                        { iconColor: 'green' },
                        onMapMarkerClick,
                    ),
                    registerPastSalesFeatures(
                        wrapperElement,
                        realtorId,
                        realtorMap,
                        OTHER_PAST_SALE_FEATURE_ID,
                        otherPastSales,
                        { iconColor: 'grey' },
                        onMapMarkerClick,
                    ),
                ]).then(() => realtorMap.fitClosestFeatures(centerCoordinates, relevantPastSales)),
            )
            .then(() =>
                realtorMap.registerExternalMarker(
                    'home-marker',
                    '/static/images/house-marker@2x.png',
                    centerCoordinates,
                ),
            )
            .then(resolve);
    }

    initializeRealtorMap(wrapperElement, realtorId, {
        center: centerCoordinates,
        onLoad,
    });
}

/**
 * Display a map with the past sales of a realtor organized regarding the context.
 * Classic: the most recent past sales of the realtor.
 * Relevant: the most relevant past sales of the realtor regarding a place.
 *
 * @param {DOMElement} wrapperElement DOM wrapper element of the map
 * @param {Function}   onMapMarkerClick Callback function invoked when map marker gets clicked
 */
export function initializePastSalesMap(wrapperElement, onMapMarkerClick) {
    if (!wrapperElement || wrapperElement.hasAttribute('data-vector-map-loaded')) {
        return;
    }

    const initializeMapFunction = wrapperElement.hasAttribute('data-relevant-past-sales-ids')
        ? initializeRelevantPastSalesMap
        : initializeClassicPastSalesMap;

    initializeMapFunction(
        wrapperElement,
        wrapperElement.getAttribute('data-realtor-id'),
        onMapMarkerClick,
    );

    wrapperElement.setAttribute('data-vector-map-loaded', true);
}

/**
 * Display realtor contact when clicking on realtor item.
 *
 * @param {String} dataAttributeName Type of realtor item being displayed Ex: 'realtor-contact'
 */
export function initializeShowRealtorDetails(dataAttributeName) {
    const $realtor = $(`[data-${dataAttributeName}-item]`);

    if ($realtor.length > 0) {
        $realtor.data('opened', false);

        $realtor.one('click', ({ target }) => {
            if ($(target).data('wa-key')) {
                let data = $(target).data('wa-data');
                data += Wa.computeData(MA.Context.waData);

                Wa.push($(target).data('wa-key'), $(target).data('wa-name'), data);
            }
        });

        $realtor.on('click', event => {
            const realtorId = $(event.target).attr('data-realtor-id');
            const $this = $(this);
            const $realtorItem = $(`[data-${dataAttributeName}-${realtorId}]`);

            sendCriteoEvent({
                event: 'viewItem',
                item: realtorId,
                atype: 'professional',
            });

            if ($realtorItem.length < 0) {
                return;
            }

            $realtorItem.toggleClass('hide');
            $(`[data-${dataAttributeName}-arrow-${realtorId}]`).toggleClass(
                'icon-expand_more--contact-open',
            );

            $this.data('opened', !$this.data('opened'));

            if (!isMobile()) {
                initializePastSalesMap($realtorItem.find('[data-vector-map]')[0]);
            }
        });
    }
}
